import { useState, useEffect } from 'preact/hooks';
import { useShallow } from 'zustand/react/shallow';
import type { AiProviderType, UserSettings } from '@voice-assistant/shared';
import { getSettings, saveApiKey, deleteApiKey, getProviderStatus, setAutoFailover, disconnectGithub, saveGithubPat, disconnectGithubPat, setGeminiDeepMode, setSttCorrectionEnabled, setSttCorrectionThinking, setPreferWhisper, setAutoSubmitVoice } from '../api/settings';
import { authFetch } from '../api/authFetch';
import { createBuyIntent, startDeploymentCheckout, type Brand } from '../api/billing';
import { VatNotice } from '../shared-ui/VatNotice';
import { getBrand } from '../brands';
import { AITuningTab } from './AITuningTab';
import { useTier } from '../hooks/useTier';
import { useSettingsStore, selectMemoSnapshotFields, type WhitelistedMemo } from '../state/settings.store';
import { useCreditsStore } from '../state/creditsStore';
import { useSwipeDownToClose } from '../hooks/useSwipeDownToClose';
import { WorkspaceModePanel } from '../shared-ui/WorkspaceModePanel';

interface Customization {
  id: string;
  type: 'instructions' | 'agent_profile';
  name: string | null;
  content: string;
  isActive: boolean;
}

interface CustomizationLimits {
  maxProfiles: number;
  maxChars: number;
  maxActive: number;
}

interface Props {
  onClose: () => void;
  memosEnabled?: boolean;
  billingEnabled?: boolean;
  readerModeEnabled?: boolean;
  logbookEnabled?: boolean;
  browserPanelEnabled?: boolean;
  /** Protofac: show the `plansSourceDir` field (Plans/Files read over SSH). SE-PF6 (#1391). */
  sandboxFsEnabled?: boolean;
  onKeyChange?: () => void;
  // P4 (#1652): TTS speed relocated here from the retired play-latest pill. Threaded
  // from App's `useTtsShared` (NOT the store) because useTTS owns its own `speedRef`
  // initialized-once-from-store — cycling via the hook keeps engine + display in sync.
  ttsSpeed?: number;
  onCycleSpeed?: () => void;
  // settings-zustand-migration Phase 4: autoSpeak / autoSubmitVoice / beetleVisible
  // and their toggle props were removed. The modal subscribes to the store directly.
}

const PROVIDERS: { type: AiProviderType; label: string; available: boolean; placeholder: string; consoleUrl?: string }[] = [
  { type: 'anthropic', label: 'Anthropic', available: true, placeholder: 'sk-ant-...' },
  { type: 'openai', label: 'OpenAI', available: false, placeholder: 'sk-...' },
  { type: 'google', label: 'Google', available: true, placeholder: 'AIza...' },
  // moonshot-byok-settings Phase 1: expose the already-built kimi BYOK path (G-T2).
  { type: 'kimi', label: 'Moonshot (Kimi)', available: true, placeholder: 'sk-...', consoleUrl: 'https://platform.moonshot.ai/console/api-keys' },
];

interface QueueStats {
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  paused: boolean;
  todayCostUsd?: number;
  dailyCapUsd?: number;
}

interface ProviderStatus {
  activeProvider: string;
  autoMode: boolean;
  anthropicHealthy: boolean;
  routerEnabled: boolean;
  lastError?: string;
  lastErrorAt?: string;
  lastFailoverReason?: string;
}

export function SettingsModal({ onClose, memosEnabled = true, billingEnabled = false, readerModeEnabled = true, logbookEnabled = true, browserPanelEnabled = true, sandboxFsEnabled = false, onKeyChange, ttsSpeed, onCycleSpeed }: Props) {
  // Phase 4: store-sourced settings (replaces 6 deleted props from App.tsx).
  // ST-4: the `autoSpeak` subscription + `toggleAutoSpeak` action that lived here backed the
  // duplicate "Playback" section. `autoPlay` now has exactly ONE control on this modal — the
  // "Sound & Playback" checkbox, which writes it through `patch()` like every other memo field.
  const autoSubmitVoice = useSettingsStore((s) => s.autoSubmitVoice === true);
  const beetleVisible = useSettingsStore((s) => s.beetleVisible);
  // Server-voice gate (SE-PF7): hide server-side voice controls (the Whisper section,
  // server TTS voice picker) when the deployment uses BROWSER-NATIVE voice only (Protofac).
  // Sourced from profile features (tts_provider / whisper_enabled) stashed by App.tsx on
  // profile load. Prod/dogfood (Piper + self-hosted Whisper) keeps them visible.
  const ttsProvider = useSettingsStore((s) => s.ttsProvider);
  const profileWhisperEnabled = useSettingsStore((s) => s.whisperEnabled);
  // SSOT for the "Auto" chat-turns hint — computed server-side from
  // backend/src/voice/turn-budget.ts, never a hardcoded literal (Lane 3, va-uiux-hardening-wave-one).
  const autoTurnBudgetHint = useSettingsStore((s) => s.autoTurnBudgetHint);
  const serverVoiceEnabled = (ttsProvider !== 'browser' && ttsProvider !== 'none') || profileWhisperEnabled;
  // Compose toggle handlers from the store actions — kept as locals so existing
  // JSX onClick handlers don't need rewriting beyond renaming.
  const handleToggleAutoSubmit = async () => {
    const next = !useSettingsStore.getState().autoSubmitVoice;
    await useSettingsStore.getState().setAutoSubmitVoice(next || null);
  };
  const setBeetleVisible = (v: boolean) => useSettingsStore.getState().setBeetleVisible(v);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [keyInputs, setKeyInputs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  // settings-zustand-migration Phase 2: reader prefs sourced from the store.
  // Phase 3 will fold the rest of this modal's local state into the store too.
  const codeBlockBehavior = useSettingsStore((s) => s.readerCodeBlocks);
  const readerCompact = useSettingsStore((s) => s.readerCompact);
  const readerSyntaxHighlight = useSettingsStore((s) => s.readerSyntaxHighlight);
  const readerFilePaths = useSettingsStore((s) => s.readerFilePaths);
  const readerEmbedMedia = useSettingsStore((s) => s.readerEmbedMedia);
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [togglingQueue, setTogglingQueue] = useState(false);
  const [providerStatus, setProviderStatus] = useState<ProviderStatus | null>(null);
  const [togglingFailover, setTogglingFailover] = useState(false);
  const [togglingBrowserAlwaysOn, setTogglingBrowserAlwaysOn] = useState(false);
  const [savingDeepMode, setSavingDeepMode] = useState(false);
  const [savingSttCorrection, setSavingSttCorrection] = useState(false);
  const [savingSttThinking, setSavingSttThinking] = useState(false);
  const [savingPreferWhisper, setSavingPreferWhisper] = useState(false);
  const [savingAutoSubmit, setSavingAutoSubmit] = useState(false);

  // Browser profile (Phase 1 hygiene — wipe + stats)
  const [browserInfo, setBrowserInfo] = useState<{
    stats: { exists: boolean; sizeBytes: number; lastAccessAt: string | null; lastModifiedAt: string | null } | null;
    browserActiveInSession: boolean;
    sessionApiReachable: boolean;
  } | null>(null);
  const [browserInfoLoading, setBrowserInfoLoading] = useState(false);
  const [browserWiping, setBrowserWiping] = useState(false);
  const [browserWipeConfirm, setBrowserWipeConfirm] = useState(false);
  const [browserWipeResult, setBrowserWipeResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Workspace (BYOM)
  const [wsStatus, setWsStatus] = useState<{ configured: boolean; host?: string; port?: number; user?: string; reposPath?: string } | null>(null);
  const [wsHost, setWsHost] = useState('');
  const [wsPort, setWsPort] = useState('22');
  const [wsUser, setWsUser] = useState('');
  const [wsReposPath, setWsReposPath] = useState('');
  const [wsCredentialId, setWsCredentialId] = useState('');
  const [wsPubKey, setWsPubKey] = useState('');
  const [wsTestResult, setWsTestResult] = useState<{ ok: boolean; error?: string; hostname?: string; nodeVersion?: string; claudeSdkVersion?: string } | null>(null);
  const [wsSaving, setWsSaving] = useState(false);
  const [wsTesting, setWsTesting] = useState(false);

  // Custom Instructions
  const [customizations, setCustomizations] = useState<Customization[]>([]);
  const [custLimits, setCustLimits] = useState<CustomizationLimits>({ maxProfiles: 1, maxChars: 200, maxActive: 1 });
  const [custInstructions, setCustInstructions] = useState('');
  const [custInstructionsId, setCustInstructionsId] = useState<string | null>(null);
  const [custSaving, setCustSaving] = useState(false);
  const [custDirty, setCustDirty] = useState(false);

  // GitHub
  const [githubDisconnecting, setGithubDisconnecting] = useState(false);
  const [patInput, setPatInput] = useState('');
  const [patSaving, setPatSaving] = useState(false);
  const [patError, setPatError] = useState<string | null>(null);
  const [patDisconnecting, setPatDisconnecting] = useState(false);
  const [patMissingScopes, setPatMissingScopes] = useState<string[]>([]);

  const REQUIRED_PAT_SCOPES = ['repo', 'read:user', 'read:org'];
  const PAT_SCOPE_DESCRIPTIONS: Record<string, string> = {
    repo: 'Read/write repos (clone, push, PR)',
    'read:user': 'Read your GitHub profile',
    'read:org': 'List organization repos',
  };
  const githubTokenUrl = `https://github.com/settings/tokens/new?scopes=${REQUIRED_PAT_SCOPES.join(',')}&description=${encodeURIComponent(getBrand().name)}`;

  // settings-zustand-migration Phase 3b: replaced boardDraft / patch / buildDraft with
  // a live store subscription + snapshot/restore.
  //
  // - `liveMemo` is the current backend memo slice from the store; reads in the
  //   render body that used `boardDraft?.field` now read from `liveMemo.field`.
  // - The "discard target" is `boardSnapshot`, owned by the store's board-edit
  //   machine (it was a `useRef` here until the board-edit FSM wave). Discard goes
  //   through the `discardChanges` / `discardAndClose` actions, which call
  //   `restoreMemoSnapshot` and drive the phase; this component never reverts
  //   directly and never holds the snapshot.
  // - `discardKey` is the store's `boardRemountKey`; it remounts the two grids that
  //   house uncontrolled `defaultValue` number inputs so their displayed value
  //   re-syncs from the restored store. Bumped by the REVERT_SUCCEEDED transition.
  // - There is no boardDraft mirror anymore — every onChange calls `patch(...)`
  //   which optimistically updates the store and PUTs /settings/memo. That makes
  //   the UX "live-apply": fields take effect as you type. Save is now a
  //   commit affordance ("yes, keep these"); Discard reverts to the snapshot.
  const liveMemo = useSettingsStore(useShallow(selectMemoSnapshotFields));
  const memoLoaded = useSettingsStore((s) => s._loaded);
  // registryDirName is now in the whitelist (this PR closed the silent-drop bug)
  // so it lives on liveMemo like the other dir-name fields. Selector retained
  // only to fall back to '' for the input value attribute.
  const registryDirName = liveMemo.registryDirName ?? '';
  // Board-edit FSM wave: this component no longer OWNS the edit session — it
  // renders it. The snapshot (was a `useRef`), the forced-re-render epoch (was a
  // dummy `useState`), the remount key, the revert-in-flight flag and the
  // close-confirm flag all live in settings.store's board-edit machine now.
  // See plans/settingsmodal-board-edit-fsm.md for the transition matrix.
  const boardPhase = useSettingsStore((s) => s.boardPhase);
  const hasBaseline = useSettingsStore((s) => s.boardSnapshot !== null);
  const discardKey = useSettingsStore((s) => s.boardRemountKey);
  const boardToast = useSettingsStore((s) => s.boardNotice);
  const boardClosePending = useSettingsStore((s) => s.boardClosePending);

  // Derived, named — no inline diff IIFE.
  //
  // `reverting` counts as dirty-shaped and as confirm-visible ON PURPOSE. A
  // revert is an in-flight request, not a resolved state: if these derivations
  // read `phase === 'dirty'` / `phase === 'confirming'` literally, the confirm
  // dialog and the save bar's affordances would UNMOUNT the instant the revert
  // starts — taking their own "Reverting…" spinner with them (the label is only
  // reachable while `boardSaving` is true, i.e. exactly while `reverting`). The
  // user would get a silent gap and then a surprise. `boardClosePending` records
  // which intent opened the revert, so only the "Discard & Close" path holds the
  // dialog up; a save-bar Discard reverts under the bar as before.
  const isBoardDirty =
    boardPhase === 'dirty' || boardPhase === 'confirming' || boardPhase === 'reverting';
  const boardSaving = boardPhase === 'reverting';
  const showCloseConfirm =
    boardPhase === 'confirming' || (boardPhase === 'reverting' && boardClosePending);

  const patch = (update: Partial<WhitelistedMemo>) => {
    // Fire-and-forget: optimistic update happens synchronously inside patchMemo;
    // server errors surface via `_lastError` on the store (shown by callers that
    // subscribe to it). We don't await here because per-field input handlers
    // mustn't block typing.
    void useSettingsStore.getState().patchMemo(update);
  };
  const { isPaidTier } = useTier();

  // Billing
  const [billingStatus, setBillingStatus] = useState<{ tier: string; hasSubscription: boolean; stripeCustomerId: string | null; brand: Brand } | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  // RD-2/D-4 (DIRECTION-CHARTER 2026-07-22): Valko is prepaid-credits-only — consumer
  // subscriptions are retired. The Valko "upgrade" affordance opens the credits top-up
  // modal (the mounted <TopUpModal/> — same v2 buy-intent → edge checkout the packs use),
  // never a subscription mint. protofac/siynergia keep the subscription buy-intent (O5-parked).
  const openTopUpModal = useCreditsStore((s) => s.openTopUpModal);
  const [billingStatusError, setBillingStatusError] = useState(false);

  // Agent Profiles
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfileContent, setNewProfileContent] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [togglingProfileId, setTogglingProfileId] = useState<string | null>(null);
  const [deletingProfileId, setDeletingProfileId] = useState<string | null>(null);

  const fetchBrowserInfo = async () => {
    setBrowserInfoLoading(true);
    try {
      const res = await authFetch('/api/browser/info');
      if (res.ok) setBrowserInfo(await res.json());
    } catch { /* ignore */ } finally {
      setBrowserInfoLoading(false);
    }
  };

  const handleBrowserWipe = async () => {
    setBrowserWiping(true);
    setBrowserWipeResult(null);
    try {
      const res = await authFetch('/api/browser/wipe', { method: 'POST' });
      if (res.ok) {
        setBrowserWipeResult({ ok: true, msg: 'Browser profile wiped. Chrome is restarting.' });
        setBrowserWipeConfirm(false);
        setTimeout(() => fetchBrowserInfo(), 5000);
      } else {
        const body = await res.json().catch(() => ({}));
        setBrowserWipeResult({ ok: false, msg: body.error || `Wipe failed (${res.status})` });
      }
    } catch (err: any) {
      setBrowserWipeResult({ ok: false, msg: err?.message || 'Network error' });
    } finally {
      setBrowserWiping(false);
    }
  };

  const fetchQueueStats = async () => {
    try {
      const res = await authFetch('/api/memos/queue/stats');
      if (res.ok) setQueueStats(await res.json());
    } catch { /* ignore */ }
  };

  const fetchProviderStatus = async () => {
    try {
      const status = await getProviderStatus();
      setProviderStatus(status);
    } catch { /* ignore */ }
  };

  const fetchCustomizations = async () => {
    try {
      const res = await authFetch('/api/customization');
      if (res.ok) {
        const data = await res.json();
        setCustomizations(data.customizations);
        setCustLimits(data.limits);
        const inst = data.customizations.find((c: Customization) => c.type === 'instructions');
        if (inst) {
          setCustInstructions(inst.content);
          setCustInstructionsId(inst.id);
        }
      }
    } catch { /* ignore */ }
  };

  const fetchWorkspaceStatus = async () => {
    try {
      const res = await authFetch('/api/workspace');
      if (res.ok) {
        const data = await res.json();
        setWsStatus(data);
        if (data.configured) {
          setWsHost(data.host || '');
          setWsPort(String(data.port || 22));
          setWsUser(data.user || '');
          setWsReposPath(data.reposPath || '');
        }
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    // Board-edit FSM wave: the machine starts here and is torn down on unmount, so
    // the next open always begins at `idle` with no inherited discard target.
    // `loadFromBackend()` below drives STORE_LOADED (baseline capture) itself —
    // there is no longer a separate snapshot-capture effect to keep in sync.
    useSettingsStore.getState().beginEditSession();
    // settings-zustand-migration Phase 3b:
    // - Store: `loadFromBackend()` populates the backend memo slice and captures
    //   the board-edit baseline as part of the same action.
    // - Legacy `getSettings()` still fires because the modal needs the non-memo
    //   fields (`keys`, `github`, `billing`, `customizations`, `buildVersion`,
    //   `githubFeatures`) which aren't (yet) part of the store. Migrating those
    //   is Phase 4. Until then both calls run in parallel.
    useSettingsStore.getState().loadFromBackend().catch(() => { /* surfaced via _lastError */ });
    getSettings()
      .then((s) => { setSettings(s); setLoading(false); })
      .catch((err) => { setError(err.message); setLoading(false); });
    fetchQueueStats();
    fetchWorkspaceStatus();
    fetchProviderStatus();
    fetchCustomizations();
    fetchBrowserInfo();
    if (billingEnabled) {
      authFetch('/api/billing/status')
        .then(r => r.ok ? r.json() : Promise.reject(new Error('status ' + r.status)))
        .then(d => { setBillingStatus(d); })
        .catch(() => { setBillingStatusError(true); });
    }
    return () => { useSettingsStore.getState().endEditSession(); };
  }, []);

  // Transient board notice auto-dismiss. The notice CONTENT is machine-owned
  // (set atomically by commitKept / the revert failure paths); only the timer
  // lives here, because a timer is a view concern, not a state region.
  useEffect(() => {
    if (!boardToast) return;
    const t = setTimeout(
      () => useSettingsStore.getState().dismissBoardNotice(),
      boardToast.ok ? 2500 : 3500,
    );
    return () => clearTimeout(t);
  }, [boardToast]);

  const handleSaveKey = async (provider: AiProviderType) => {
    const key = keyInputs[provider]?.trim();
    if (!key) return;
    setSaving(provider);
    setError(null);
    try {
      await saveApiKey(provider, key);
      setKeyInputs((prev) => ({ ...prev, [provider]: '' }));
      const updated = await getSettings();
      setSettings(updated);
      onKeyChange?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteKey = async (provider: AiProviderType) => {
    setSaving(provider);
    setError(null);
    try {
      await deleteApiKey(provider);
      const updated = await getSettings();
      setSettings(updated);
      onKeyChange?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  // handleSetProvider / the AI Provider radio retired (model-meta-hardening
  // Phase 6b) — provider is now derived per-session from the session model id.

  const hasKey = (provider: AiProviderType) =>
    settings?.keys.find((k) => k.provider === provider)?.hasKey ?? false;

  // The dirty diff moved into the machine (`settleDirty` in settings.store), so
  // every patch re-derives the phase in one place instead of every render
  // recomputing an anonymous IIFE the tests could never address by name.

  const guardedClose = () => {
    // The machine decides: clean → it transitions to `closed` and returns true;
    // dirty → it raises the confirm and returns false. The caller never inspects
    // dirty state itself, so ✕ / overlay / swipe / Escape cannot drift apart.
    if (useSettingsStore.getState().requestClose()) onClose();
  };

  // unified-drawer §8/P3: Settings now renders as the unified bottom-sheet drawer.
  // Swipe-down dismisses through guardedClose so the unsaved-changes guard still fires.
  const sheetSwipe = useSwipeDownToClose(guardedClose);

  // ── MODAL-STACK, second half (§9.2 W2 → carried into W3/W4, issue #1966) ──────────────
  // W2 fixed Escape for the command palette but deliberately left Settings Escape-inert,
  // because the obvious wiring is a TRAP: a shell-level `Escape → closeSettings` bypasses
  // guardedClose() and SILENTLY DISCARDS the user's edits. An overlay that ignores Escape is
  // an a11y defect; an overlay that loses work on Escape is a data-loss defect. So the
  // handler lives HERE, inside the component that owns the guard, and routes through the
  // exact same function the ✕ button and the swipe-down dismiss already use.
  //
  //   dirty   → guardedClose() raises the "Discard changes?" confirm; Settings stays open.
  //   clean   → guardedClose() closes (nothing to lose).
  //   confirm already up → Escape means "Keep Editing", i.e. dismiss the CONFIRM, never fall
  //                        through to a close. Escape must not become a second, quieter path
  //                        to the very outcome the confirm exists to make deliberate.
  //
  // Registered on `document` (not the card) so it works wherever focus sits, mirroring the
  // W2 CommandPalette rationale: the escape hatch must not depend on a focus trap being
  // airtight. Effect deps re-register per render so the handler never reads a stale
  // isBoardDirty. `stopPropagation` keeps a single Escape from also reaching shell-level
  // handlers; the ⌘K-inert path (settingsOpenRef in DesktopShell) is untouched.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (showCloseConfirm) { useSettingsStore.getState().keepEditing(); return; }
      guardedClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  return (
    <div class="settings-overlay" onClick={(e) => { if (e.target === e.currentTarget) guardedClose(); }}>
      <div class="settings-modal">
        <div class="drawer-grab" aria-hidden="true" onTouchStart={sheetSwipe.onTouchStart} onTouchEnd={sheetSwipe.onTouchEnd} />
        <div class="settings-header">
          <h2>Settings</h2>
          <button class="settings-close" onClick={guardedClose}>✕</button>
        </div>

        {loading && <div class="settings-loading">Loading...</div>}
        {error && <div class="settings-error">{error}</div>}

        {/* Board settings toast */}
        {boardToast && (
          <div class={`settings-board-toast${boardToast.ok ? '' : ' settings-board-toast--err'}`}>
            {boardToast.ok ? '✓ ' : '✕ '}{boardToast.msg}
          </div>
        )}

        {/* Exit confirm dialog (Phase 3b: changes are already live in the store —
            "Save & Close" just closes; "Discard & Close" reverts via the snapshot). */}
        {showCloseConfirm && (
          <div class="settings-confirm-overlay">
            <div class="settings-confirm-box">
              <p class="settings-confirm-title">Discard changes?</p>
              <p class="settings-confirm-msg">
                Settings apply as you change them. Keep your edits, or revert to how things were when you opened settings?
              </p>
              <div class="settings-confirm-actions">
                <button
                  class="settings-btn save"
                  disabled={boardSaving}
                  onClick={() => { useSettingsStore.getState().keepAndClose(); onClose(); }}
                >Keep & Close</button>
                <button
                  class="settings-btn"
                  disabled={boardSaving}
                  onClick={async () => {
                    // ONE store action for one intent (element 4): revert the
                    // server values AND end the session. It returns false when the
                    // revert failed, and we then stay open — the old code closed
                    // regardless, stranding the user with the very edits they
                    // asked to drop still live on the server.
                    if (await useSettingsStore.getState().discardAndClose()) onClose();
                  }}
                >{boardSaving ? 'Reverting…' : 'Discard & Close'}</button>
                <button
                  class="settings-btn"
                  disabled={boardSaving}
                  onClick={() => useSettingsStore.getState().keepEditing()}
                >Keep Editing</button>
              </div>
            </div>
          </div>
        )}

        {settings && (
          <>
            {/* ST-4 (§9.1, W4): the "Playback" section that stood here was a SECOND control
                for `autoPlay` — the same backend memo field the Interface group's "Sound &
                Playback" checkbox writes, under a different name ("Auto-speak") and in a
                different place. Two switches for one setting: flip either and the other moves,
                which reads as a bug whichever one you found first. Consolidated into "Sound &
                Playback" below (the checkbox + the speed control that used to live here). */}
            {/* ══ GROUP: Workspace ═══════════════════════════════ */}
            <div class="settings-group-header"><span>Workspace</span></div>

            {/* ── Mode chooser + own-box consent (BYO-box relay #1062) ──────────
                Sits ABOVE the BYOM form on purpose: choosing where the agent runs is
                the decision that gives the host/port/user form its meaning. */}
            <WorkspaceModePanel />

            {/* ── Workspace (BYOM) ─────────────────────────────── */}
            <div class="settings-section">
              <h3>Workspace (BYOM)</h3>
              {wsStatus?.configured ? (
                <div class="ws-status">
                  <p class="ws-connected">Connected: <code>{wsStatus.user}@{wsStatus.host}:{wsStatus.port}</code></p>
                  <p class="settings-hint">Repos path: <code>{wsStatus.reposPath}</code></p>
                  <div class="settings-row" style={{ gap: '8px', marginTop: '8px' }}>
                    <button class="settings-btn" disabled={wsTesting} onClick={async () => {
                      setWsTesting(true); setWsTestResult(null);
                      try {
                        const res = await authFetch('/api/workspace/test', { method: 'POST' });
                        setWsTestResult(await res.json());
                      } catch (e: any) { setWsTestResult({ ok: false, error: e.message }); }
                      setWsTesting(false);
                    }}>{wsTesting ? 'Testing...' : 'Test Connection'}</button>
                    <button class="settings-btn settings-btn--danger" onClick={async () => {
                      if (!confirm('Remove workspace configuration?')) return;
                      await authFetch('/api/workspace', { method: 'DELETE' });
                      setWsStatus({ configured: false }); setWsCredentialId(''); setWsPubKey('');
                    }}>Remove</button>
                  </div>
                  {wsTestResult && (
                    <div class={`ws-test-result ${wsTestResult.ok ? 'ws-test-ok' : 'ws-test-fail'}`}>
                      {wsTestResult.ok ? (
                        <><strong>Connected</strong> — {wsTestResult.hostname}{wsTestResult.nodeVersion ? `, Node ${wsTestResult.nodeVersion}` : ''}{wsTestResult.claudeSdkVersion ? `, Claude SDK ${wsTestResult.claudeSdkVersion}` : ''}</>
                      ) : (
                        <><strong>Failed</strong> — {wsTestResult.error}</>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div class="ws-setup">
                  {!wsCredentialId ? (
                    <>
                      <p class="settings-hint">Connect your development machine so {getBrand().name} can run Claude Code on your projects.</p>
                      <button class="settings-btn" disabled={wsSaving} onClick={async () => {
                        setWsSaving(true);
                        try {
                          const res = await authFetch('/api/workspace/generate-keypair', { method: 'POST' });
                          const data = await res.json();
                          if (data.ok) { setWsCredentialId(data.credentialId); setWsPubKey(data.publicKey); }
                          else setError(data.message || 'Failed to generate keypair');
                        } catch (e: any) { setError(e.message); }
                        setWsSaving(false);
                      }}>{wsSaving ? 'Generating...' : 'Step 1: Generate SSH Key'}</button>
                    </>
                  ) : !wsStatus?.configured ? (
                    <>
                      <p class="settings-hint"><strong>Step 2:</strong> Add this public key to <code>~/.ssh/authorized_keys</code> on your machine:</p>
                      <div class="ws-pubkey-box">
                        <textarea readOnly rows={3} value={wsPubKey} onClick={(e) => (e.target as HTMLTextAreaElement).select()} />
                        <button class="settings-btn settings-btn--small" onClick={() => { navigator.clipboard.writeText(wsPubKey); }}>Copy</button>
                      </div>
                      <p class="settings-hint"><strong>Step 3:</strong> Enter your SSH connection details:</p>
                      <div class="ws-form">
                        <label>Host <input type="text" value={wsHost} onInput={(e) => setWsHost((e.target as HTMLInputElement).value)} placeholder="192.168.1.14" /></label>
                        <label>Port <input type="number" value={wsPort} onInput={(e) => setWsPort((e.target as HTMLInputElement).value)} placeholder="22" /></label>
                        <label>User <input type="text" value={wsUser} onInput={(e) => setWsUser((e.target as HTMLInputElement).value)} placeholder="akrsmv" /></label>
                        <label>Projects path <input type="text" value={wsReposPath} onInput={(e) => setWsReposPath((e.target as HTMLInputElement).value)} placeholder="/home/user/projects" /></label>
                      </div>
                      <button class="settings-btn" disabled={wsSaving || !wsHost || !wsUser} onClick={async () => {
                        setWsSaving(true); setWsTestResult(null);
                        try {
                          const res = await authFetch('/api/workspace/configure', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ host: wsHost, port: parseInt(wsPort) || 22, user: wsUser, reposPath: wsReposPath || undefined, credentialId: wsCredentialId }),
                          });
                          const data = await res.json();
                          if (data.ok) {
                            // Auto-test after configuring
                            const testRes = await authFetch('/api/workspace/test', { method: 'POST' });
                            setWsTestResult(await testRes.json());
                            fetchWorkspaceStatus();
                          } else setError(data.message || 'Failed to save');
                        } catch (e: any) { setError(e.message); }
                        setWsSaving(false);
                      }}>{wsSaving ? 'Saving...' : 'Save & Test Connection'}</button>
                      {wsTestResult && (
                        <div class={`ws-test-result ${wsTestResult.ok ? 'ws-test-ok' : 'ws-test-fail'}`}>
                          {wsTestResult.ok ? <><strong>Connected!</strong> Workspace ready.</> : <><strong>Failed</strong> — {wsTestResult.error}</>}
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </div>

            {/* ══ GROUP: Agent Behavior ══════════════════════════ */}
            <div class="settings-group-header"><span>Agent Behavior</span></div>

            {/* ── Custom Instructions ─────────────────────────── */}
            <div class="settings-section">
              <h3>Custom Instructions</h3>
              <p class="settings-hint">
                These instructions are injected into every AI session. Tell {getBrand().name} about your preferences, coding style, or role.
              </p>
              <textarea
                class="cust-instructions-textarea"
                rows={4}
                maxLength={custLimits.maxChars}
                placeholder="e.g. I prefer TypeScript with strict mode. Always explain trade-offs before making architectural decisions."
                value={custInstructions}
                onInput={(e) => {
                  setCustInstructions((e.target as HTMLTextAreaElement).value);
                  setCustDirty(true);
                }}
              />
              <div class="cust-instructions-footer">
                <span class="cust-char-count">{custInstructions.length}/{custLimits.maxChars}</span>
                <button
                  class="settings-btn save"
                  disabled={!custDirty || custSaving}
                  onClick={async () => {
                    setCustSaving(true);
                    try {
                      await authFetch('/api/customization', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          id: custInstructionsId || undefined,
                          type: 'instructions',
                          content: custInstructions,
                        }),
                      });
                      setCustDirty(false);
                      await fetchCustomizations();
                    } catch (err: any) { setError(err.message); }
                    setCustSaving(false);
                  }}
                >{custSaving ? 'Saving...' : 'Save'}</button>
              </div>
            </div>

            {/* ── Agent Profiles ───────────────────────────────── */}
            <div class="settings-section">
              {(() => {
                const profiles = customizations.filter((c) => c.type === 'agent_profile');
                const activeCount = profiles.filter((p) => p.isActive).length;
                const atMax = profiles.length >= custLimits.maxProfiles;
                const atActiveMax = activeCount >= custLimits.maxActive;
                return (
                  <>
                    <div class="agent-profiles-header">
                      <h3>Agent Profiles</h3>
                      <span class="agent-profiles-badge">
                        {activeCount}/{custLimits.maxActive} active · {profiles.length}/{custLimits.maxProfiles} total
                      </span>
                    </div>
                    <p class="settings-hint">
                      Named personas injected alongside your instructions. Switch context without rewriting — e.g. "Senior Reviewer", "Explain Like I'm 5", "Focus: backend only".
                    </p>

                    {profiles.length > 0 && (
                      <div class="agent-profile-list">
                        {profiles.map((p) => (
                          <div key={p.id} class={`agent-profile-card ${p.isActive ? 'active' : ''}`}>
                            <div class="agent-profile-card-top">
                              <span class="agent-profile-name">{p.name || 'Unnamed'}</span>
                              <div class="agent-profile-actions">
                                <button
                                  class={`agent-profile-toggle ${p.isActive ? 'on' : 'off'}`}
                                  disabled={togglingProfileId === p.id || (!p.isActive && atActiveMax)}
                                  title={!p.isActive && atActiveMax ? `Max ${custLimits.maxActive} active profiles` : p.isActive ? 'Deactivate' : 'Activate'}
                                  onClick={async () => {
                                    setTogglingProfileId(p.id);
                                    try {
                                      await authFetch(`/api/customization/${p.id}/toggle`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ isActive: !p.isActive }),
                                      });
                                      await fetchCustomizations();
                                    } catch (err: any) { setError(err.message); }
                                    setTogglingProfileId(null);
                                  }}
                                >{p.isActive ? 'On' : 'Off'}</button>
                                <button
                                  class="agent-profile-delete"
                                  disabled={deletingProfileId === p.id}
                                  onClick={async () => {
                                    if (!confirm(`Delete profile "${p.name}"?`)) return;
                                    setDeletingProfileId(p.id);
                                    try {
                                      await authFetch(`/api/customization/${p.id}`, { method: 'DELETE' });
                                      await fetchCustomizations();
                                    } catch (err: any) { setError(err.message); }
                                    setDeletingProfileId(null);
                                  }}
                                >✕</button>
                              </div>
                            </div>
                            <p class="agent-profile-preview">{p.content}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {showAddProfile ? (
                      <div class="agent-profile-add-form">
                        <input
                          class="agent-profile-name-input"
                          type="text"
                          placeholder="Profile name (e.g. Senior Reviewer)"
                          maxLength={60}
                          value={newProfileName}
                          onInput={(e) => setNewProfileName((e.target as HTMLInputElement).value)}
                        />
                        <textarea
                          class="cust-instructions-textarea"
                          rows={3}
                          maxLength={custLimits.maxChars}
                          placeholder="e.g. You are a senior engineer. Always suggest the simplest possible solution and flag over-engineering."
                          value={newProfileContent}
                          onInput={(e) => setNewProfileContent((e.target as HTMLTextAreaElement).value)}
                        />
                        <div class="cust-instructions-footer">
                          <span class="cust-char-count">{newProfileContent.length}/{custLimits.maxChars}</span>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button class="settings-btn" onClick={() => { setShowAddProfile(false); setNewProfileName(''); setNewProfileContent(''); }}>Cancel</button>
                            <button
                              class="settings-btn save"
                              disabled={profileSaving || !newProfileName.trim() || !newProfileContent.trim()}
                              onClick={async () => {
                                setProfileSaving(true);
                                try {
                                  await authFetch('/api/customization', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ type: 'agent_profile', name: newProfileName.trim(), content: newProfileContent.trim() }),
                                  });
                                  setShowAddProfile(false);
                                  setNewProfileName('');
                                  setNewProfileContent('');
                                  await fetchCustomizations();
                                } catch (err: any) { setError(err.message); }
                                setProfileSaving(false);
                              }}
                            >{profileSaving ? 'Saving...' : 'Save Profile'}</button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        class="settings-btn agent-profile-add-btn"
                        disabled={atMax}
                        title={atMax ? `Max ${custLimits.maxProfiles} profiles on your plan` : 'Add agent profile'}
                        onClick={() => setShowAddProfile(true)}
                      >+ Add Profile{atMax ? ` (${custLimits.maxProfiles}/${custLimits.maxProfiles})` : ''}</button>
                    )}
                  </>
                );
              })()}
            </div>

            {/* ══ GROUP: AI & Model ══════════════════════════════ */}
            <div class="settings-group-header"><span>AI &amp; Model</span></div>

            {/* The per-user "AI Provider" radio was retired (model-meta-hardening
                Phase 6b) — provider is now derived per-session from the session's
                model (chosen at session creation), so there is no global default
                to pick here. Per-provider API keys live in the "API Keys" section
                below; auto-failover (router state) remains. */}

            {providerStatus?.routerEnabled && (
              <div class="settings-section">
                <h3>Provider Failover</h3>
                <div class="settings-memo-grid">
                  <label class="settings-memo-row">
                    <span>Auto-failover to Gemini when Claude is unavailable</span>
                    <input
                      type="checkbox"
                      checked={providerStatus.autoMode}
                      disabled={togglingFailover}
                      onChange={async (e) => {
                        const enabled = (e.target as HTMLInputElement).checked;
                        setTogglingFailover(true);
                        try {
                          await setAutoFailover(enabled);
                          await fetchProviderStatus();
                        } catch { /* ignore */ }
                        setTogglingFailover(false);
                      }}
                    />
                  </label>
                </div>
                {/* The "Active: …" provider pill was removed (model-meta-hardening
                    Phase 6b): provider is now per-session, and this global Settings
                    modal has no session context, so a single "active provider" value
                    is no longer meaningful. The per-turn provider is shown in the
                    session's TurnMetaDrawer instead. Auto-failover (above) is router
                    state and stays. */}
                <p class="settings-hint">
                  When enabled, Claude rate limits, overloads, or billing issues automatically switch
                  to Gemini for that turn. Provider for each session is chosen by its model.
                </p>
              </div>
            )}

            <div class="settings-section">
              <h3>API Keys</h3>
              {PROVIDERS.filter((p) => p.available).map((p) => (
                <div key={p.type} class="settings-key-row">
                  <div class="settings-key-label">
                    <span>{p.label}</span>
                    {p.consoleUrl && (
                      <a href={p.consoleUrl} target="_blank" rel="noopener noreferrer">get a key ↗</a>
                    )}
                    {hasKey(p.type) ? (
                      <span class="settings-key-status set">&#10003; stored</span>
                    ) : (
                      <span class="settings-key-status">not set</span>
                    )}
                  </div>
                  <div class="settings-key-input-row">
                    <input
                      type="password"
                      placeholder={p.placeholder}
                      value={keyInputs[p.type] || ''}
                      onInput={(e) => setKeyInputs((prev) => ({
                        ...prev,
                        [p.type]: (e.target as HTMLInputElement).value,
                      }))}
                      class="settings-key-input"
                    />
                    <button
                      class="settings-btn save"
                      disabled={!keyInputs[p.type]?.trim() || saving === p.type}
                      onClick={() => handleSaveKey(p.type)}
                    >
                      {saving === p.type ? '…' : 'Save'}
                    </button>
                    {hasKey(p.type) && (
                      <button
                        class="settings-btn delete"
                        disabled={saving === p.type}
                        onClick={() => handleDeleteKey(p.type)}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {memoLoaded && (
              <div class="settings-section">
                <h3>AI Tuning</h3>
                {/* Phase 3a: draft/patch props removed — AITuningTab subscribes to useSettingsStore directly. */}
                <AITuningTab isPaidTier={isPaidTier} />
              </div>
            )}

            {(settings.githubFeatures?.oauthEnabled || settings.githubFeatures?.patEnabled) && (
            <div class="settings-section">
              <h3>GitHub</h3>
              {settings.github?.activeMethod !== 'none' && (
                <p class="settings-hint" style={{ marginBottom: 8 }}>
                  GITHUB_TOKEN is automatically injected into agent sessions — the agent can clone, push, and use the <code>gh</code> CLI.
                  {settings.github?.activeMethod === 'pat' && settings.github?.oauth?.connected && (
                    <> Active method: fine-grained token (preferred).</>
                  )}
                </p>
              )}

              {/* OAuth section */}
              {settings.githubFeatures?.oauthEnabled && (() => {
                const oauth = settings.github?.oauth;
                const showLabel = settings.githubFeatures?.oauthEnabled && settings.githubFeatures?.patEnabled;
                return (
                  <div style={{ marginBottom: 12 }}>
                    {showLabel && <p class="settings-hint" style={{ fontWeight: 600, marginBottom: 4 }}>Easy Connect (OAuth)</p>}
                    {oauth?.connected ? (
                      <div class="settings-github-connected">
                        {oauth.avatarUrl && (
                          <img src={oauth.avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />
                        )}
                        <span class="settings-key-status set">&#10003; Connected as @{oauth.login}</span>
                        <button
                          class="settings-btn delete"
                          disabled={githubDisconnecting}
                          onClick={async () => {
                            setGithubDisconnecting(true);
                            try { await disconnectGithub(); const u = await getSettings(); setSettings(u); } catch { /* ignore */ }
                            setGithubDisconnecting(false);
                          }}
                        >
                          {githubDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                        </button>
                      </div>
                    ) : (
                      <div>
                        {!showLabel && (
                          <p class="settings-hint">Connect your GitHub account so the agent can clone repos, create branches, and open pull requests on your behalf.</p>
                        )}
                        <a href="/api/github" class="settings-btn save" style={{ display: 'inline-block', textDecoration: 'none', marginTop: 4 }}>
                          Connect GitHub
                        </a>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* PAT section */}
              {settings.githubFeatures?.patEnabled && (() => {
                const pat = settings.github?.pat;
                const showLabel = settings.githubFeatures?.oauthEnabled && settings.githubFeatures?.patEnabled;
                return (
                  <div>
                    {showLabel && <p class="settings-hint" style={{ fontWeight: 600, marginBottom: 4 }}>Fine-grained Token (PAT)</p>}
                    {pat?.connected ? (
                      <div class="settings-github-connected">
                        <span class="settings-key-status set">&#10003; Connected as @{pat.login} <span style={{ opacity: 0.6, fontSize: '0.85em' }}>{pat.tokenPreview}</span></span>
                        <button
                          class="settings-btn delete"
                          disabled={patDisconnecting}
                          onClick={async () => {
                            setPatDisconnecting(true);
                            setPatMissingScopes([]);
                            try { await disconnectGithubPat(); const u = await getSettings(); setSettings(u); } catch { /* ignore */ }
                            setPatDisconnecting(false);
                          }}
                        >
                          {patDisconnecting ? 'Removing…' : 'Remove'}
                        </button>
                        {patMissingScopes.length > 0 && (
                          <p class="settings-hint" style={{ color: 'var(--text-secondary)', marginTop: 6, marginBottom: 0 }}>
                            &#9888; Missing scopes: <strong>{patMissingScopes.join(', ')}</strong>. Some features may not work (e.g. org repo access). <a href={githubTokenUrl} target="_blank" rel="noopener noreferrer">Regenerate with all scopes</a>.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        {/* Scope checklist */}
                        <div style={{ marginBottom: 8 }}>
                          <p class="settings-hint" style={{ marginBottom: 4 }}>
                            Create a <strong>classic token</strong> with these scopes, then paste it below:
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 8 }}>
                            {REQUIRED_PAT_SCOPES.map(scope => (
                              <div key={scope} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85em' }}>
                                <code style={{ background: 'var(--bg-surface)', borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace' }}>{scope}</code>
                                <span style={{ opacity: 0.7 }}>{PAT_SCOPE_DESCRIPTIONS[scope]}</span>
                              </div>
                            ))}
                          </div>
                          <a
                            href={githubTokenUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="settings-btn save"
                            style={{ display: 'inline-block', textDecoration: 'none', marginBottom: 8 }}
                          >
                            Generate token on GitHub &#8599;
                          </a>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input
                            type="password"
                            class="settings-input"
                            placeholder="ghp_… or github_pat_…"
                            value={patInput}
                            onInput={(e) => { setPatInput((e.target as HTMLInputElement).value); setPatError(null); }}
                            style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.85em' }}
                          />
                          <button
                            class="settings-btn save"
                            disabled={patSaving || !patInput.trim()}
                            onClick={async () => {
                              setPatSaving(true);
                              setPatError(null);
                              setPatMissingScopes([]);
                              try {
                                const result = await saveGithubPat(patInput);
                                if (result.valid) {
                                  setPatInput('');
                                  if (result.missingScopes?.length) setPatMissingScopes(result.missingScopes);
                                  const u = await getSettings();
                                  setSettings(u);
                                } else {
                                  setPatError(result.reason ?? 'Invalid token');
                                }
                              } catch (err: any) {
                                setPatError(err.message ?? 'Failed to save');
                              }
                              setPatSaving(false);
                            }}
                          >
                            {patSaving ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                        {patError && <p class="settings-hint" style={{ color: 'var(--va-danger, var(--accent-red))', marginTop: 4 }}>{patError}</p>}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            )}

            {/* ══ GROUP: Account ════════════════════════════════ */}
            {billingEnabled && <div class="settings-group-header"><span>Account</span></div>}

            {billingEnabled && (
            <div class="settings-section">
              <h3>{billingStatus?.brand === 'valko' ? 'Credits' : 'Subscription'}</h3>
              {billingStatus ? (
                billingStatus.hasSubscription ? (
                  <div>
                    <p style={{ margin: '0 0 8px' }}>
                      Current plan: <strong style={{ textTransform: 'capitalize' }}>{billingStatus.tier}</strong>
                      {billingStatus.tier === 'pro' && ' ($9.99/mo)'}
                      {billingStatus.tier === 'managed' && ' ($39.99/mo)'}
                    </p>
                    <button
                      class="settings-btn save"
                      disabled={billingLoading}
                      onClick={async () => {
                        setBillingLoading(true);
                        try {
                          const res = await authFetch('/api/billing/portal', { method: 'POST' });
                          if (!res.ok) throw new Error('Failed to open billing portal');
                          const { portalUrl } = await res.json();
                          window.location.href = portalUrl;
                        } catch { setBillingLoading(false); }
                      }}
                    >
                      {billingLoading ? 'Opening…' : 'Manage Subscription'}
                    </button>
                    <p class="settings-hint" style={{ marginTop: 6 }}>Change plan, update payment method, or cancel via Stripe Customer Portal.</p>
                  </div>
                ) : billingStatus.brand === 'valko' ? (
                  // RD-2/D-4: Valko monetization is prepaid credits only — no consumer subscription.
                  // The affordance opens the credits top-up modal (v2 buy-intent → edge checkout), the
                  // same rail the credit packs use; STRIPE_PRICE_VALKO is never touched.
                  <div>
                    <p style={{ margin: '0 0 8px' }}>{getBrand().name} runs on <strong>prepaid credits</strong> — you only pay for what you use.</p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        class="settings-btn save"
                        disabled={billingLoading}
                        onClick={() => openTopUpModal(null)}
                      >
                        Buy {getBrand().name} credits
                      </button>
                    </div>
                    <p class="settings-hint" style={{ marginTop: 8 }}>
                      Top up your balance in seconds to unlock unlimited sessions, Vertex AI STT, smart context, and more.
                      You'll be taken to secure checkout.
                    </p>
                    {/* Stripe Tax P3: the VAT basis at the buy button — a notice, not a gate (SE-03). */}
                    <VatNotice />
                  </div>
                ) : (
                  <div>
                    <p style={{ margin: '0 0 8px' }}>Current plan: <strong>Free</strong></p>
                    {/* Buy-loop §D2: one brand subscription per deployment. The button mints a signed
                        buy-intent for THIS deployment's brand → redirects to the inctasoft-cf edge (or the
                        Customer Portal if already subscribed). Price is resolved server-side at the edge.
                        Valko is handled by the credits branch above (RD-2 — no consumer subscription). */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        class="settings-btn save"
                        disabled={billingLoading}
                        onClick={async () => {
                          setBillingLoading(true);
                          try {
                            const { url } = await createBuyIntent(billingStatus.brand, 'pro');
                            window.location.href = url;
                          } catch { setBillingLoading(false); }
                        }}
                      >
                        {billingLoading
                          ? 'Redirecting…'
                          : `Upgrade to ${billingStatus.brand.charAt(0).toUpperCase() + billingStatus.brand.slice(1)}`}
                      </button>
                    </div>
                    <p class="settings-hint" style={{ marginTop: 8 }}>
                      Unlock the full subscription — unlimited sessions, Vertex AI STT, smart context, and more.
                      You'll be taken to secure checkout.
                    </p>
                    <VatNotice />
                  </div>
                )
              ) : billingStatusError ? (
                <p class="settings-hint" style={{ color: 'var(--va-danger, var(--accent-red))' }}>Could not load billing status — try reopening Settings.</p>
              ) : (
                <p class="settings-hint">Loading billing status…</p>
              )}
            </div>
            )}

            {memosEnabled && (<>
            <div class="settings-section">
              <h3>Board</h3>
              {memoLoaded && (() => {
                const d = liveMemo;
                return (
                  <>
                    <div class="settings-memo-grid" key={discardKey}>
                      <label class="settings-memo-row">
                        <span>Plan model</span>
                        <select
                          class="settings-select"
                          value={d.planModel}
                          onChange={(e) => patch({ planModel: (e.target as HTMLSelectElement).value as 'sonnet' | 'opus' })}
                        >
                          <option value="opus">Opus (best plans, ~$1.24/group)</option>
                          <option value="sonnet">Sonnet (cheaper, ~$0.06/group)</option>
                        </select>
                      </label>
                      <label class="settings-memo-row">
                        <span>Context depth (lines)</span>
                        <input
                          type="number"
                          class="settings-number"
                          min="20"
                          max="500"
                          defaultValue={d.contextLines}
                          onBlur={(e) => { const v = parseInt((e.target as HTMLInputElement).value, 10); if (v >= 20 && v <= 500) patch({ contextLines: v }); else (e.target as HTMLInputElement).value = String(d.contextLines); }}
                        />
                      </label>
                      <label class="settings-memo-row">
                        <span>Max chat turns</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <select
                            class="settings-select"
                            value={d.maxChatTurns === 0 ? 'off' : d.maxChatTurns === -1 ? 'auto' : 'manual'}
                            onChange={(e) => {
                              const v = (e.target as HTMLSelectElement).value;
                              patch({ maxChatTurns: v === 'off' ? 0 : v === 'auto' ? -1 : 30 });
                            }}
                          >
                            <option value="off">Disabled</option>
                            <option value="auto">Auto (context-aware)</option>
                            <option value="manual">Manual</option>
                          </select>
                          {(d.maxChatTurns ?? 0) > 0 && (
                            <input
                              type="number"
                              class="settings-number"
                              min="1"
                              max="200"
                              defaultValue={d.maxChatTurns}
                              onBlur={(e) => { const v = parseInt((e.target as HTMLInputElement).value, 10); if (v >= 1 && v <= 200) patch({ maxChatTurns: v }); else (e.target as HTMLInputElement).value = String(d.maxChatTurns); }}
                            />
                          )}
                          {d.maxChatTurns === 0 && <span class="settings-hint" style={{ margin: 0 }}>Context % threshold only</span>}
                          {d.maxChatTurns === -1 && (
                            <span class="settings-hint" style={{ margin: 0 }}>
                              {autoTurnBudgetHint != null ? `~${autoTurnBudgetHint} turns (1M ctx)` : 'context-aware'}
                            </span>
                          )}
                        </div>
                      </label>
                      <label class="settings-memo-row">
                        <span>Auto-triage after transcription</span>
                        <input
                          type="checkbox"
                          checked={d.autoTriage}
                          onChange={(e) => patch({ autoTriage: (e.target as HTMLInputElement).checked })}
                        />
                      </label>
                      <label class="settings-memo-row">
                        <span>Transcription language</span>
                        <select
                          class="settings-select"
                          value={d.memoLanguage}
                          onChange={(e) => patch({ memoLanguage: (e.target as HTMLSelectElement).value })}
                        >
                          <option value="en">English</option>
                          <option value="bg">Bulgarian</option>
                        </select>
                      </label>
                      <label class="settings-memo-row">
                        <span>Auto-pin to plan after transcription</span>
                        <input
                          type="checkbox"
                          checked={d.autoPin}
                          onChange={(e) => patch({ autoPin: (e.target as HTMLInputElement).checked })}
                        />
                      </label>
                      <label class="settings-memo-row">
                        <span>Auto-replan when threshold reached</span>
                        <input
                          type="checkbox"
                          checked={d.autoReplanEnabled}
                          onChange={(e) => patch({ autoReplanEnabled: (e.target as HTMLInputElement).checked })}
                        />
                      </label>
                      <label class="settings-memo-row">
                        <span>Auto-replan threshold</span>
                        <input
                          type="number"
                          class="settings-number"
                          min="1"
                          max="20"
                          defaultValue={d.autoReplanThreshold}
                          disabled={!d.autoReplanEnabled}
                          onBlur={(e) => { const v = parseInt((e.target as HTMLInputElement).value, 10); if (v >= 1 && v <= 20) patch({ autoReplanThreshold: v }); else (e.target as HTMLInputElement).value = String(d.autoReplanThreshold); }}
                        />
                      </label>
                      <label class="settings-memo-row">
                        <span>Auto docs-update + push at context %</span>
                        <input
                          type="checkbox"
                          checked={d.autoDocsUpdate}
                          onChange={(e) => patch({ autoDocsUpdate: (e.target as HTMLInputElement).checked })}
                        />
                      </label>
                      <label class="settings-memo-row">
                        <span>Context threshold (%)</span>
                        <input
                          type="number"
                          class="settings-number"
                          min="50"
                          max="95"
                          defaultValue={d.autoDocsUpdateThreshold}
                          disabled={!d.autoDocsUpdate}
                          onBlur={(e) => { const v = parseInt((e.target as HTMLInputElement).value, 10); if (v >= 50 && v <= 95) patch({ autoDocsUpdateThreshold: v }); else (e.target as HTMLInputElement).value = String(d.autoDocsUpdateThreshold); }}
                        />
                      </label>
                      <label class="settings-memo-row">
                        <span>Auto-continue after session reset</span>
                        <input
                          type="checkbox"
                          checked={d.autoContinueOnReset}
                          disabled={!d.autoDocsUpdate}
                          onChange={(e) => patch({ autoContinueOnReset: (e.target as HTMLInputElement).checked })}
                        />
                      </label>
                      <label class="settings-memo-row">
                        <span title="Adds ~1s and ~$0.001 per reset. Generates a structured TL;DR before auto-reset so the next agent can continue seamlessly.">Generate handoff on auto-reset</span>
                        <input
                          type="checkbox"
                          checked={d.handoffOnReset ?? true}
                          disabled={!d.autoContinueOnReset}
                          onChange={(e) => patch({ handoffOnReset: (e.target as HTMLInputElement).checked })}
                        />
                      </label>
                      <label class="settings-memo-row">
                        <span>Message retention (days, 0 = forever)</span>
                        <input
                          type="number"
                          class="settings-number"
                          min="0"
                          max="365"
                          defaultValue={d.messageRetentionDays ?? 30}
                          onBlur={(e) => { const v = parseInt((e.target as HTMLInputElement).value, 10); if (!isNaN(v) && v >= 0 && v <= 365) patch({ messageRetentionDays: v }); else (e.target as HTMLInputElement).value = String(d.messageRetentionDays ?? 30); }}
                        />
                      </label>
                    </div>
                  </>
                );
              })()}
              <p class="settings-hint">
                <strong>Context depth</strong> — how many lines of each project's CLAUDE.md are injected when planning from memos. Higher = richer context, more tokens.
                <strong>Max chat turns</strong> — when checked, the agent resets after this many turns (saves cost, forces knowledge persistence). Uncheck for no limit.
                Auto-pin skips the manual pin step — memos go straight to plan after transcription.
                Auto-replan triggers automatic re-planning when this many memos are pending.
                Auto docs-update persists knowledge to git when context usage hits the threshold.
                Auto-continue sends a follow-up prompt after reset so the agent picks up seamlessly.
              </p>
            </div>
            </>)}
            {/* ↑ ST-2 (§9.1, W4): the `memosEnabled` gate ENDS HERE, at the end of the Board
                section — the only section on this modal that is actually about memos.
                It used to run ~670 lines further, all the way past Diagram Style, so
                `memos_enabled: false` (its value on ALL 12 installation profiles — see
                backend/config/profiles/*.yaml) silently stripped Sound & Playback, Navigation,
                Speech Input, Browser Tools, Gemini Deep Mode, STT correction, Whisper,
                Autonomous Execution, Reader, Logbook, Reader Mode and Diagram Style from every
                deployment. Each of those either has its OWN feature gate (browserPanelEnabled,
                waveExecutionEnabled, readerModeEnabled, logbookEnabled, whisper) or is core UX
                that no flag should hide. A gate must hide its own feature and nothing else.
                SE-DS-15 enumerates the sections so this cannot silently regrow. */}

            {/* ══ GROUP: Interface ═══════════════════════════════ */}
            <div class="settings-group-header"><span>Interface</span></div>

            <div class="settings-section">
              <h3>Sound &amp; Playback</h3>
              <div class="settings-memo-grid">
                <label class="settings-memo-row">
                  <span>Auto-play responses</span>
                  <input
                    type="checkbox"
                    checked={liveMemo.autoPlay ?? true}
                    onChange={(e) => patch({ autoPlay: (e.target as HTMLInputElement).checked })}
                  />
                </label>
              </div>
              <p class="settings-hint">
                When on, TTS starts automatically as soon as an agent response arrives. Toggle the 🔊 tab button to mute individual sessions without changing this setting.
              </p>
              {/* ST-4: the read-aloud speed control, relocated from the deleted duplicate
                  "Playback" section so every playback knob sits in one place. */}
              {onCycleSpeed && ttsSpeed !== undefined && (
                <div class="settings-row">
                  <button
                    class="settings-toggle-btn"
                    onClick={() => onCycleSpeed()}
                    aria-label={`Read-aloud speed ${ttsSpeed}x — tap to cycle`}
                  >
                    ⏩ Speed: {ttsSpeed}x
                  </button>
                  <p class="settings-hint">Playback speed for auto-spoken responses. (Per-message 🔊 reads have their own speed control in the playback bar.)</p>
                </div>
              )}
            </div>

            <div class="settings-section">
              <h3>Navigation</h3>
              <div class="settings-memo-grid">
                <label class="settings-memo-row">
                  <span>Back gesture returns to session list</span>
                  <input
                    type="checkbox"
                    checked={liveMemo.osBackToSessionList ?? true}
                    onChange={(e) => patch({ osBackToSessionList: (e.target as HTMLInputElement).checked })}
                  />
                </label>
              </div>
              <p class="settings-hint">
                When on (default), pressing the OS back button or swiping back from any chat session goes straight to the session list — one press, one jump. Turn off to walk back through every visited session in order, one press at a time.
              </p>
            </div>

            <div class="settings-section">
              <h3>Speech Input</h3>
              <p class="settings-hint">
                Uses your device's built-in speech recognition (Google on Android Chrome, Siri on iOS Safari) — no audio leaves your phone. Zero cost, instant results.
              </p>
            </div>

            {/* ── Browser Tools (gated on browser_panel_enabled) ─────────────
                  Previously this section was a kitchen-sink containing
                  Gemini Deep Mode, STT correction, Whisper toggles, and
                  per-project autonomous execution. Each of those moved to
                  its own section below so the Browser gate doesn't hide
                  unrelated settings when a profile turns Browser off. */}
            {browserPanelEnabled && (
            <div class="settings-section" data-feature-gate="browser_panel_enabled">
              <h3>Browser Tools</h3>
              <div class="settings-memo-grid">
                <label class="settings-memo-row">
                  <span>Always load browser tools</span>
                  <input
                    type="checkbox"
                    checked={settings?.browserAlwaysOn ?? false}
                    disabled={togglingBrowserAlwaysOn}
                    onChange={async (e) => {
                      const enabled = (e.target as HTMLInputElement).checked;
                      setTogglingBrowserAlwaysOn(true);
                      try {
                        await authFetch('/api/settings/browser-always-on', {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ enabled }),
                        });
                        setSettings((s) => s ? { ...s, browserAlwaysOn: enabled } : s);
                      } catch { /* ignore */ }
                      setTogglingBrowserAlwaysOn(false);
                    }}
                  />
                </label>
              </div>
              <p class="settings-hint">
                When on, Playwright browser tools are ready from the first message. When off (default), they activate only when you say "screenshot", "browse to", "check the page", etc.
              </p>

              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border, rgba(255,255,255,0.08))' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <strong style={{ fontSize: '0.95em' }}>Browser data</strong>
                  {browserInfoLoading && <span class="settings-hint" style={{ fontSize: '0.8em' }}>loading…</span>}
                </div>
                {browserInfo === null && !browserInfoLoading && (
                  <p class="settings-hint" style={{ margin: 0 }}>Browser service unreachable — cannot show profile info.</p>
                )}
                {browserInfo && !browserInfo.sessionApiReachable && (
                  <p class="settings-hint" style={{ margin: 0 }}>Browser service unreachable.</p>
                )}
                {browserInfo && browserInfo.sessionApiReachable && browserInfo.stats && !browserInfo.stats.exists && (
                  <p class="settings-hint" style={{ margin: 0 }}>No browser profile on disk yet — you haven't used the browser tool.</p>
                )}
                {browserInfo && browserInfo.stats?.exists && (
                  <>
                    <p class="settings-hint" style={{ margin: 0 }}>
                      Size: <strong>{Math.max(1, Math.round(browserInfo.stats.sizeBytes / 1024 / 1024))} MB</strong>
                      {' · '}
                      Last used: <strong>{browserInfo.stats.lastModifiedAt ? new Date(browserInfo.stats.lastModifiedAt).toLocaleDateString() : 'unknown'}</strong>
                    </p>
                    <p class="settings-hint" style={{ margin: '6px 0 10px' }}>
                      Anything the agent's browser does — including sites you signed into — is stored on our servers in this profile. Wiping clears all cookies, logins, and cached data for the browser tool.
                    </p>
                    {!browserWipeConfirm && !browserWiping && (
                      <button
                        type="button"
                        onClick={() => { setBrowserWipeResult(null); setBrowserWipeConfirm(true); }}
                        style={{
                          padding: '6px 12px',
                          background: 'transparent',
                          border: '1px solid var(--accent-danger, #d77757)',
                          color: 'var(--accent-danger, #d77757)',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: '0.9em',
                        }}
                      >
                        Wipe browser state
                      </button>
                    )}
                    {browserWipeConfirm && !browserWiping && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span class="settings-hint" style={{ margin: 0 }}>
                          This will close any open browser pages and sign you out of everything. Continue?
                        </span>
                        <button
                          type="button"
                          onClick={handleBrowserWipe}
                          style={{
                            padding: '6px 12px',
                            background: 'var(--accent-danger, #d77757)',
                            border: 'none',
                            color: 'white',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: '0.9em',
                          }}
                        >
                          Yes, wipe
                        </button>
                        <button
                          type="button"
                          onClick={() => setBrowserWipeConfirm(false)}
                          style={{
                            padding: '6px 12px',
                            background: 'transparent',
                            border: '1px solid var(--border, rgba(255,255,255,0.2))',
                            color: 'inherit',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: '0.9em',
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    {browserWiping && (
                      <p class="settings-hint" style={{ margin: 0 }}>Wiping… Chrome is restarting.</p>
                    )}
                    {browserWipeResult && (
                      <p
                        class="settings-hint"
                        style={{
                          margin: '8px 0 0',
                          color: browserWipeResult.ok ? 'var(--accent-success, #8fbf7f)' : 'var(--accent-danger, #d77757)',
                        }}
                      >
                        {browserWipeResult.msg}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            )}

            {/* ── Gemini Deep Mode ───────────────────────────────────────── */}
            <div class="settings-section">
              <h3>Gemini Deep Mode</h3>
              <div class="settings-memo-grid">
                <label class="settings-memo-row">
                  <span>Mode {savingDeepMode && <span class="settings-hint" style={{ fontSize: '0.8em' }}>saving…</span>}</span>
                  <select
                    value={settings?.geminiDeepMode ?? 'detected'}
                    disabled={savingDeepMode}
                    onChange={async (e) => {
                      const mode = (e.target as HTMLSelectElement).value as 'off' | 'detected' | 'all';
                      setSavingDeepMode(true);
                      try {
                        await setGeminiDeepMode(mode);
                        setSettings((s) => s ? { ...s, geminiDeepMode: mode } : s);
                      } catch { /* ignore */ }
                      setSavingDeepMode(false);
                    }}
                  >
                    <option value="off">Off — no extra context</option>
                    <option value="detected">Detected — active project's CLAUDE.md (default)</option>
                    <option value="all">All — every workspace CLAUDE.md (higher cost)</option>
                  </select>
                </label>
              </div>
              <p class="settings-hint">
                Loads the active project's full <code>CLAUDE.md</code> (or every project's, in <em>all</em> mode) into Gemini's per-session context. Default <em>detected</em> is safe for everyone; <em>all</em> can drive ~$0.20/query on Pro.
              </p>
            </div>

            {/* ── STT correction ─────────────────────────────────────────── */}
            <div class="settings-section">
              <h3>STT correction</h3>
              <div class="settings-memo-grid">
                <label class="settings-memo-row">
                  <span>Fix technical terms in transcripts {savingSttCorrection && <span class="settings-hint" style={{ fontSize: '0.8em' }}>saving…</span>}</span>
                  <input
                    type="checkbox"
                    checked={settings?.sttCorrectionEnabled !== false}
                    disabled={savingSttCorrection}
                    onChange={async (e) => {
                      const enabled = (e.target as HTMLInputElement).checked;
                      const value: boolean | null = enabled ? null : false;
                      setSavingSttCorrection(true);
                      try {
                        await setSttCorrectionEnabled(value);
                        setSettings((s) => s ? { ...s, sttCorrectionEnabled: value } : s);
                      } catch { /* ignore */ }
                      setSavingSttCorrection(false);
                    }}
                  />
                </label>
              </div>
              <p class="settings-hint">
                When on (default), Whisper transcripts are post-processed by Gemini Flash to fix terms like "nest j s" → "NestJS" or "by ok" → "BYOK". Turn off to see raw Whisper output.
              </p>
              {settings?.sttCorrectionEnabled !== false && (
                <div style={{ marginTop: 10 }}>
                  <div class="settings-memo-grid">
                    <label class="settings-memo-row">
                      <span>
                        Thinking mode{' '}
                        <span class="settings-hint" style={{ fontWeight: 'normal' }}>~20× cost, may restructure phrasing</span>
                      </span>
                      <input
                        type="checkbox"
                        checked={!!settings?.sttCorrectionThinking}
                        disabled={savingSttThinking}
                        onChange={async (e) => {
                          const on = (e.target as HTMLInputElement).checked;
                          const value: boolean | null = on ? true : null;
                          setSavingSttThinking(true);
                          try {
                            await setSttCorrectionThinking(value);
                            setSettings((s) => s ? { ...s, sttCorrectionThinking: value } : s);
                          } catch { /* ignore */ }
                          setSavingSttThinking(false);
                        }}
                      />
                    </label>
                  </div>
                  <p class="settings-hint" style={{ marginTop: 4 }}>
                    Lets Gemini reason about your speech before correcting — can clarify filler words and improve phrasing. Check <code>stt_correction_log</code> to compare quality.
                  </p>
                </div>
              )}
            </div>

            {/* ── Whisper (server-side STT) — shown only when this deployment uses a server
                  voice path. Protofac (browser-native voice only) hides it entirely. SE-PF7. ── */}
            {serverVoiceEnabled && settings?.whisperEnabled && (
              <div class="settings-section">
                <h3>Whisper</h3>
                <div class="settings-memo-grid">
                  <label class="settings-memo-row">
                    <span>Always use Whisper (skip device speech recognition) {savingPreferWhisper && <span class="settings-hint" style={{ fontSize: '0.8em' }}>saving…</span>}</span>
                    <input
                      type="checkbox"
                      checked={settings?.preferWhisper === true}
                      disabled={savingPreferWhisper}
                      onChange={async (e) => {
                        const enabled = (e.target as HTMLInputElement).checked;
                        setSavingPreferWhisper(true);
                        try {
                          await setPreferWhisper(enabled ? true : null);
                          setSettings((s) => s ? { ...s, preferWhisper: enabled ? true : null } : s);
                        } catch { /* ignore */ }
                        setSavingPreferWhisper(false);
                      }}
                    />
                  </label>
                </div>
                <p class="settings-hint">
                  When on, always record audio and send to the server for transcription (Whisper), bypassing the device's built-in speech recognition. Enable this if the default mic button does nothing or fails silently.
                </p>

                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border, rgba(255,255,255,0.08))' }}>
                  <div class="settings-memo-grid">
                    <label class="settings-memo-row">
                      <span>Auto-send after transcription {savingAutoSubmit && <span class="settings-hint" style={{ fontSize: '0.8em' }}>saving…</span>}</span>
                      <input
                        type="checkbox"
                        checked={autoSubmitVoice === true}
                        disabled={savingAutoSubmit}
                        onChange={handleToggleAutoSubmit}
                      />
                    </label>
                  </div>
                  <p class="settings-hint" style={{ marginTop: 6 }}>
                    When on, transcribed voice messages are sent automatically without stopping to review.
                  </p>
                </div>
              </div>
            )}

            {/* ── Autonomous Execution — Projects (wave-execution-gated) ── */}
            {settings?.waveExecutionEnabled && (
              <div class="settings-section">
                <h3>Autonomous Execution — Projects</h3>
                <p class="settings-hint" style={{ margin: '0 0 10px' }}>
                  Control which projects can be included in Wave executions. Disabled projects are skipped when starting a wave.
                </p>
                {(['voice-assistant', 'reverse-proxy', 'accounting', 'car-expenses'] as const).map((projectKey) => {
                  const overrides = settings?.perProjectAutonomousOverrides ?? null;
                  const isEnabled = overrides && Object.prototype.hasOwnProperty.call(overrides, projectKey)
                    ? overrides[projectKey]
                    : true;
                  return (
                    <div key={projectKey} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                      <span style={{ fontSize: '0.88em', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{projectKey}</span>
                      <label class="settings-toggle" style={{ margin: 0 }}>
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={async (e) => {
                            const enabled = (e.target as HTMLInputElement).checked;
                            const value = enabled ? null : false;
                            try {
                              const { authFetch } = await import('../api/authFetch');
                              await authFetch('/api/settings/project-autonomous', {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ projectKey, enabled: value }),
                              });
                              setSettings((s) => {
                                if (!s) return s;
                                const current = { ...(s.perProjectAutonomousOverrides ?? {}) };
                                if (enabled) { delete current[projectKey]; }
                                else { current[projectKey] = false; }
                                return { ...s, perProjectAutonomousOverrides: Object.keys(current).length > 0 ? current : null };
                              });
                            } catch { /* ignore */ }
                          }}
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Reader (#1140) — gated SOLELY on reader_mode_enabled.
                  Previously this section's outer gate was (reader || logbook),
                  so Logbook=on forced the entire Reader settings to render
                  even when Reader Mode was disabled. Now each feature gates
                  its own Settings section. */}
            {readerModeEnabled && (
            <div class="settings-section" data-feature-gate="reader_mode_enabled">
              <h3>Reader</h3>
              <div class="settings-memo-grid">
                <label class="settings-memo-row">
                  <span>Compact system messages</span>
                  <input
                    type="checkbox"
                    checked={readerCompact}
                    onChange={(e) => {
                      useSettingsStore.getState().setReaderCompact((e.target as HTMLInputElement).checked);
                      window.dispatchEvent(new Event('reader-settings-changed'));
                    }}
                  />
                </label>
                <label class="settings-memo-row">
                  <span>Syntax highlighting</span>
                  <input
                    type="checkbox"
                    checked={readerSyntaxHighlight}
                    onChange={(e) => {
                      useSettingsStore.getState().setReaderSyntaxHighlight((e.target as HTMLInputElement).checked);
                      window.dispatchEvent(new Event('reader-settings-changed'));
                    }}
                  />
                </label>
                <label class="settings-memo-row">
                  <span>Clickable file paths</span>
                  <input
                    type="checkbox"
                    checked={readerFilePaths}
                    onChange={(e) => {
                      useSettingsStore.getState().setReaderFilePaths((e.target as HTMLInputElement).checked);
                      window.dispatchEvent(new Event('reader-settings-changed'));
                    }}
                  />
                </label>
                <label class="settings-memo-row">
                  <span>Auto-embed media</span>
                  <input
                    type="checkbox"
                    checked={readerEmbedMedia}
                    onChange={(e) => {
                      useSettingsStore.getState().setReaderEmbedMedia((e.target as HTMLInputElement).checked);
                      window.dispatchEvent(new Event('reader-settings-changed'));
                    }}
                  />
                </label>
              </div>
              <p class="settings-hint">
                Controls how agent messages are displayed. Compact system messages hide AI complexity behind friendly labels. Syntax highlighting colors code blocks. File paths become clickable links to the Code Viewer. Media embeds auto-play video and audio inline.
              </p>
            </div>
            )}

            {/* ── Logbook (#1140) — its own section, gated SOLELY on logbook_enabled.
                  Was previously a nested row inside the Reader section, which made
                  it follow Reader's gate too. */}
            {logbookEnabled && (
            <div class="settings-section" data-feature-gate="logbook_enabled">
              <h3>🐞 Logbook</h3>
              <div class="settings-memo-grid">
                <label class="settings-memo-row">
                  <span>🐞 Logbook beetle FAB</span>
                  <input
                    type="checkbox"
                    checked={beetleVisible}
                    onChange={(e) => setBeetleVisible((e.target as HTMLInputElement).checked)}
                  />
                </label>
              </div>
              <p class="settings-hint">
                Show the floating beetle (🐞) to capture screenshots + notes into the Logbook.
              </p>
            </div>
            )}

            {/* ── Reader Mode (#1140) — gated by reader_mode_enabled.
                  Was unconditionally rendered before — Brato-only feature leaked
                  into every profile's Settings panel. */}
            {readerModeEnabled && (
            <div class="settings-section" data-feature-gate="reader_mode_enabled">
              <h3>📖 Reader Mode</h3>
              <div class="settings-memo-grid">
                <label class="settings-memo-row">
                  <span>Code blocks</span>
                  <select
                    class="settings-input"
                    style={{ width: 'auto' }}
                    value={codeBlockBehavior}
                    onChange={(e) => {
                      const v = (e.target as HTMLSelectElement).value;
                      if (v === 'skip' || v === 'announce' || v === 'full') {
                        useSettingsStore.getState().setReaderCodeBlocks(v);
                      }
                    }}
                  >
                    <option value="skip">Skip silently</option>
                    <option value="announce">Announce ("Code block, N lines")</option>
                    <option value="full">Read code fully</option>
                  </select>
                </label>
              </div>
              <p class="settings-hint">
                Controls how code blocks are read aloud by Reader Mode (📖). "Announce" says "Code block, N lines" instead of reading raw code. "Skip" silently skips all code blocks. "Read fully" reads the raw code text.
              </p>
            </div>
            )}

            {/* ── Diagram Style ─────────────────────────────── */}
            <div class="settings-section">
              <h3>🎨 Diagram Style</h3>
              <div class="settings-row" style={{ alignItems: 'center', gap: '8px' }}>
                <select
                  class="settings-input"
                  style={{ flex: 1 }}
                  value={liveMemo.diagramStyle || 'auto'}
                  onChange={(e) => {
                    const val = (e.target as HTMLSelectElement).value as 'auto' | 'minimal' | 'rich' | 'vibrant';
                    patch({ diagramStyle: val });
                    document.documentElement.setAttribute('data-diagram-style', val);
                  }}
                >
                  <option value="auto">Auto (follows app theme)</option>
                  <option value="minimal">Minimal — clean, light tones</option>
                  <option value="rich">Rich — deep, saturated dark</option>
                  <option value="vibrant">Vibrant — bold, colorful</option>
                </select>
              </div>
              <p class="settings-hint">
                Controls the color palette used for Mermaid diagrams (sequence, flowchart, gantt, etc.). Auto selects the best-matching preset for your current app theme. All presets meet WCAG AA contrast standards for readable text.
              </p>
            </div>

            {/* ── Files Drawer ──────────────────────────── (always visible, no memosEnabled gate) */}
            <div class="settings-section">
              <h3>📁 Files Drawer</h3>

              {/* Always-on built-in directories */}
              <p class="settings-hint" style={{ marginBottom: 8 }}>
                Five directories are <strong>always active</strong> — no configuration needed. The drawer scans your entire workspace for them automatically.
              </p>
              <div class="settings-drawer-builtins">
                <div class="settings-drawer-builtin-row">
                  <span>📄</span><code>docs</code><span>Reference documentation</span>
                </div>
                <div class="settings-drawer-builtin-row">
                  <span>📋</span><code>plans</code><span>Implementation plans</span>
                </div>
                <div class="settings-drawer-builtin-row">
                  <span>🗄️</span><code>registry</code><span>Service config, test specs &amp; developer registry</span>
                </div>
                <div class="settings-drawer-builtin-row">
                  <span>📂</span><code>_operational_docs</code><span>Working files &amp; agent outputs</span>
                </div>
                <div class="settings-drawer-builtin-row">
                  <span>🗂️</span><code>_archive/</code><span>Within each directory above — follows <code>/plan</code> convention</span>
                </div>
              </div>

              {/* Override names — collapsible */}
              <details class="settings-drawer-override">
                <summary>Override directory names</summary>
                <div class="settings-drawer-dirs" style={{ marginTop: 8 }}>
                  <label class="settings-drawer-dir-row">
                    <span class="settings-drawer-dir-icon">📂</span>
                    <span class="settings-drawer-dir-label">Operational Docs</span>
                    <input type="text" class="settings-input settings-drawer-dir-input"
                      placeholder="_operational_docs"
                      value={liveMemo.tempDocsDirName ?? ''}
                      onInput={(e) => patch({ tempDocsDirName: (e.target as HTMLInputElement).value })}
                    />
                    <span class="settings-drawer-dir-hint">Leave blank to use default</span>
                  </label>
                  <label class="settings-drawer-dir-row">
                    <span class="settings-drawer-dir-icon">📋</span>
                    <span class="settings-drawer-dir-label">Plans</span>
                    <input type="text" class="settings-input settings-drawer-dir-input"
                      placeholder="plans"
                      value={liveMemo.plansDirName ?? ''}
                      onInput={(e) => patch({ plansDirName: (e.target as HTMLInputElement).value })}
                    />
                    <span class="settings-drawer-dir-hint">Leave blank to use default</span>
                  </label>
                  <label class="settings-drawer-dir-row">
                    <span class="settings-drawer-dir-icon">📄</span>
                    <span class="settings-drawer-dir-label">Project Docs</span>
                    <input type="text" class="settings-input settings-drawer-dir-input"
                      placeholder="docs"
                      value={liveMemo.projectDocsDirName ?? ''}
                      onInput={(e) => patch({ projectDocsDirName: (e.target as HTMLInputElement).value })}
                    />
                    <span class="settings-drawer-dir-hint">Leave blank to use default</span>
                  </label>
                  <label class="settings-drawer-dir-row">
                    <span class="settings-drawer-dir-icon">🧪</span>
                    <span class="settings-drawer-dir-label">Registry</span>
                    <input type="text" class="settings-input settings-drawer-dir-input"
                      placeholder="registry"
                      value={registryDirName}
                      onInput={(e) => patch({ registryDirName: (e.target as HTMLInputElement).value })}
                    />
                    <span class="settings-drawer-dir-hint">tests.yaml, services.yaml, per-project specs</span>
                  </label>
                </div>
              </details>

              {/* SE-PF6 (#1391): sandbox plans source dir — only on sandbox-FS profiles (protofac*).
                  Absolute path inside the user's own sandbox container; Plans/Files read over SSH. */}
              {sandboxFsEnabled && (
                <div class="settings-drawer-dirs" style={{ marginTop: 12 }}>
                  <label class="settings-drawer-dir-row">
                    <span class="settings-drawer-dir-icon">📡</span>
                    <span class="settings-drawer-dir-label">Plans source (sandbox)</span>
                    <input type="text" class="settings-input settings-drawer-dir-input"
                      placeholder="/home/valko/projects/my-app/plans"
                      value={liveMemo.plansSourceDir ?? ''}
                      onInput={(e) => patch({ plansSourceDir: (e.target as HTMLInputElement).value })}
                    />
                    <span class="settings-drawer-dir-hint">Absolute path in your sandbox. Plans tab reads .md files here over SSH. Leave blank to disable.</span>
                  </label>
                </div>
              )}

              {/* Pro+ additional custom directories — upgrade prompt only for free-tier users */}
              {billingEnabled && !billingStatus?.hasSubscription && (
              <div class="settings-drawer-pro-section">
                <div class="settings-drawer-pro-header">
                  <span>Additional custom directories</span>
                  <span class="settings-tier-badge">Pro+</span>
                </div>
                <p class="settings-hint">Scan extra workspace directories beyond the four built-ins.</p>
                <div class="settings-drawer-upgrade-prompt">
                  <span>{billingStatus?.brand === 'valko' ? 'Buy credits to add custom directories' : 'Upgrade to Pro to add custom directories'}</span>
                  {billingStatus?.brand === 'valko' ? (
                    // RD-2/D-4: Valko has no Pro subscription — route the gated affordance to the
                    // credits top-up modal (same rail as the Subscription/Credits section above).
                    <button
                      class="settings-drawer-upgrade-btn"
                      disabled={billingLoading}
                      onClick={() => openTopUpModal(null)}
                    >
                      Buy credits →
                    </button>
                  ) : (
                    <button
                      class="settings-drawer-upgrade-btn"
                      disabled={billingLoading}
                      onClick={async () => {
                        setBillingLoading(true);
                        try {
                          // Buy-loop §D2: mint for this deployment's brand (resolved server-side) → redirect.
                          const { url } = await startDeploymentCheckout('pro');
                          window.location.href = url;
                        } catch { setBillingLoading(false); }
                      }}
                    >
                      {billingLoading ? 'Redirecting…' : 'Upgrade →'}
                    </button>
                  )}
                </div>
                <VatNotice />
              </div>
              )}
            </div>

            {memosEnabled && (<>
            <div class="settings-section">
              <h3>Autonomous Pipeline</h3>
              <div class="settings-memo-grid">
                <label class="settings-memo-row">
                  <span>Pipeline {queueStats?.paused === false ? 'running' : 'paused'}</span>
                  <input
                    type="checkbox"
                    checked={queueStats ? !queueStats.paused : false}
                    disabled={togglingQueue || !queueStats}
                    onChange={async (e) => {
                      const enable = (e.target as HTMLInputElement).checked;
                      setTogglingQueue(true);
                      try {
                        await authFetch(`/api/memos/queue/${enable ? 'resume' : 'pause'}`, {
                          method: 'POST',
                        });
                        await fetchQueueStats();
                      } catch { /* ignore */ }
                      setTogglingQueue(false);
                    }}
                  />
                </label>
              </div>
              {queueStats && (
                <div class="pipeline-stats">
                  <span class="pipeline-stat">
                    <span class="pipeline-stat-dot" style={{ background: queueStats.paused ? '#666' : '#10b981' }} />
                    {queueStats.active} active
                  </span>
                  <span class="pipeline-stat">{queueStats.waiting} queued</span>
                  <span class="pipeline-stat">{queueStats.completed} done</span>
                  {queueStats.failed > 0 && <span class="pipeline-stat" style={{ color: '#ef4444' }}>{queueStats.failed} failed</span>}
                  {queueStats.todayCostUsd != null && (
                    <span class="pipeline-stat">
                      ${queueStats.todayCostUsd.toFixed(2)} / ${queueStats.dailyCapUsd?.toFixed(2)} today
                    </span>
                  )}
                </div>
              )}
              <div class="settings-memo-grid" key={`${discardKey}-pipeline`} style={{ marginTop: '10px' }}>
                <label class="settings-memo-row">
                  <span>Planner turns (Opus)</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <select
                      class="settings-select"
                      value={(liveMemo.maxPlannerTurns ?? 100) === 0 ? 'off' : (liveMemo.maxPlannerTurns ?? 100) === -1 ? 'auto' : 'manual'}
                      onChange={(e) => {
                        const v = (e.target as HTMLSelectElement).value;
                        patch({ maxPlannerTurns: v === 'off' ? 0 : v === 'auto' ? -1 : 50 });
                      }}
                    >
                      <option value="off">Disabled</option>
                      <option value="auto">Auto</option>
                      <option value="manual">Manual</option>
                    </select>
                    {(liveMemo.maxPlannerTurns ?? 100) > 0 && (
                      <input
                        type="number"
                        class="settings-number"
                        min="1"
                        max="200"
                        value={liveMemo.maxPlannerTurns ?? ''}
                        onInput={(e) => { const v = parseInt((e.target as HTMLInputElement).value, 10); if (v >= 1 && v <= 200) patch({ maxPlannerTurns: v }); }}
                      />
                    )}
                  </div>
                </label>
                <label class="settings-memo-row">
                  <span>Worker turns (Sonnet)</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <select
                      class="settings-select"
                      value={(liveMemo.maxWorkerTurns ?? 100) === 0 ? 'off' : (liveMemo.maxWorkerTurns ?? 100) === -1 ? 'auto' : 'manual'}
                      onChange={(e) => {
                        const v = (e.target as HTMLSelectElement).value;
                        patch({ maxWorkerTurns: v === 'off' ? 0 : v === 'auto' ? -1 : 50 });
                      }}
                    >
                      <option value="off">Disabled</option>
                      <option value="auto">Auto</option>
                      <option value="manual">Manual</option>
                    </select>
                    {(liveMemo.maxWorkerTurns ?? 100) > 0 && (
                      <input
                        type="number"
                        class="settings-number"
                        min="1"
                        max="200"
                        value={liveMemo.maxWorkerTurns ?? ''}
                        onInput={(e) => { const v = parseInt((e.target as HTMLInputElement).value, 10); if (v >= 1 && v <= 200) patch({ maxWorkerTurns: v }); }}
                      />
                    )}
                  </div>
                </label>
              </div>
              <p class="settings-hint">
                When enabled, queued chunks execute automatically via DTM orchestrator (falls back to local queue if DTM is unavailable). Disabling pauses execution — in-flight chunks finish, but no new ones start.
              </p>
            </div>
            </>)}
            {memoLoaded && hasBaseline && (
              <div class={`board-save-bar${isBoardDirty ? ' board-save-bar--dirty' : ''}`}>
                {isBoardDirty && <span class="board-save-bar__label">Unsaved changes</span>}
                <button
                  class="settings-btn save"
                  disabled={!isBoardDirty || boardSaving}
                  onClick={() => {
                    // Phase 3b: every onChange already PUT'd to the backend via
                    // patchMemo (no debounce). "Save" sends nothing — it re-baselines
                    // the snapshot so "Unsaved changes" clears. ST-6 (§9.1, W4)
                    // retired the "Settings saved" copy because it was a lie; the
                    // notice text now lives with the transition that sets it.
                    // Re-baseline + notice are one atomic action (element 4).
                    useSettingsStore.getState().commitKept();
                  }}
                >{boardSaving ? 'Saving…' : 'Save'}</button>
                {isBoardDirty && (
                  <button
                    class="settings-btn"
                    disabled={boardSaving}
                    onClick={() => {
                      // Revert to the baseline and stay open. The machine re-takes
                      // the snapshot and bumps the remount key (which re-syncs the
                      // uncontrolled `defaultValue` number-input grids) as part of
                      // the REVERT_SUCCEEDED transition; on failure it lands back in
                      // `dirty` with the notice set.
                      void useSettingsStore.getState().discardChanges();
                    }}
                  >Discard</button>
                )}
              </div>
            )}
          </>
        )}
        {settings?.buildVersion && (
          <div class="settings-build-version">build {settings.buildVersion}</div>
        )}
      </div>
    </div>
  );
}
